/** @module csi/xml/Writer */
define([
	"dojo/_base/array",
	"dojo/_base/declare",
	"dojo/_base/lang",
	"csi",
	"csi/comm",
	"csi/comm/PostRequest",
	"csi/constants",
	"csi/service/openTelemetryService",
	"csi/util",
	"csi/utility/apiUtility",
	"csi/xml",
	"./CallMethod",
	"./ChangeNode",
	"./Query"
], function(array, declare, lang, csi, comm, PostRequest, constants, openTelemetryService, util, apiUtility, xml, CallMethod, ChangeNode, Query) {

	const CALL_LIMIT = 1000;
	const CHANGE_LIMIT = 5000;
	const CREATE_LIMIT = 1000;
	const DELETE_LIMIT = 1000;
	const NODES_OPERATIONS = "NODES_OPERATIONS";
	const NODE_PROCESSOR = "NodeProcessor";
	const EXECUTE = "Execute";
	const FORMAT_VERSION = "FormatVersion";
	const UPDATED_NODES = "UpdatedNodes";
	const CHANGE_NODE = "ChangeNode";
	const CREATE_NODE = "CreateNode";
	const RETURN_NODES = "ReturnNodes";
	const DEFAULT_PARAMS = {
		[RETURN_NODES]: "All"
	};

	function _ignoreUsageOnDelete(doc, node) {
		node = xml.e(doc, comm.PARAMETER, node);
		node.setAttribute("Id", "CheckUsage");
		node.setAttribute("Type", "boolean");
		node.setAttribute("Value", "false");
	}

	return declare(null, {
		// Boolean: indicates if writer contains changes that cannot act on cache
		//bCannotCache: false,

		// Boolean: indicates whether or not there are any operations to post
		//bChanged: false,

		/** @property {boolean} bReport - indicates that the writer is being used for reporting (changes will not be presisted) */
		bReport: false,

		// Boolean: indicates whether or not writer is shared amongst action controllers, such as to hold changes
		//bShared: false,

		// Cache: cache where changes should be mirrored so client can work without server response
		//cache: null,

		/** @property {(hasChanged: boolean) => void} onChanged - function to call after writer has been changed */
		onChanged: null,

		/** @property {boolean} resetAfterExecute - indicates that the writer should be reset (cleared) once executed */
		resetAfterExecute: true,

		// ChangeNode: holds on to last change node to determine if there was any change
		//_cnLast: null,

		// Map<String, SpanContext>: the map of linked span contexts, keys are spanId-s
		//_linkedSpans: null,

		constructor: function(/*Object?*/params) {
			// create document to hold XML node operations
			this.doc = util.newXMLDocument();

			// add root element
			const rootElement = this.doc.createElement(NODES_OPERATIONS);
			rootElement.setAttribute(FORMAT_VERSION, constants.FMT_VERSION_VALUE);
			this.doc.appendChild(rootElement);

			// create request for publishing
			this.m_request = new PostRequest(NODE_PROCESSOR, EXECUTE, lang.mixin(params || {}, DEFAULT_PARAMS));

			this._linkedSpans = new Map();
			this._captureCurrentSpanContext();
		},

		/**
		 * Write comment to XML
		 * @param {string} text 
		 * @return {this}
		 */
		addComment(text) {
			const {doc} = this;
			doc.documentElement.appendChild(doc.createTextNode("\n"));
			doc.documentElement.appendChild(doc.createTextNode("\n"));
			doc.documentElement.appendChild(doc.createComment(text));
			return this;
		},

		/**
		 * Take operations from given writer and add to this writer; the given writer will be reset
		 * @param {Writer} writer
		 * @return {this}
		 */
		addOperationsFrom(writer) {
			// add linked spans from the passed writer into a current writer
			writer._linkedSpans.forEach((value, key) => this._linkedSpans.set(key, value));
			this._captureCurrentSpanContext();

			const {documentElement} = this.doc;
			const {documentElement: sourceElement} = writer.doc;

			while (sourceElement.firstElementChild) {
				documentElement.appendChild(sourceElement.firstElementChild);
				this.setChanged(true);
			}

			writer.reset();
			return this;
		},

		addParameter: function(key, value, bAllowDuplicate) {
			// summary: add parameter to POST request
			this._captureCurrentSpanContext();

			this.m_request.addParameter(key, value, bAllowDuplicate);
			return this;
		},

		canCacheChanges: function() {
			// summary: if not caching changes, then this does not apply
			return this.cache ? !this.bCannotCache : true;
		},

		execute: function(/*Object?*/ioArgs) {
			// summary: submit post request, but before doing so, reset the writer so operations already published are not resubmitted
			const request = this.finalizeRequest();
			let spanOptions = {};

			const linkedSpans = this._linkedSpans;
			if (linkedSpans.size) {
				spanOptions.links = [...linkedSpans.values()].map((context) => ({context}))
			}

			if (this.resetAfterExecute) {
				this.reset();
			}

			const span = openTelemetryService.startSpan("c8.web.writer", spanOptions);
			return openTelemetryService.executeInContext(span, () => {
				const isSync = ioArgs?.sync;
				const response = request.post(ioArgs);

				if (isSync) {
					span.end();
					return response;
				}

				return response.then((response) => {
					span.end();
					return response;
				})
			});
		},

		/**
		 * Finalize request to be sent to server
		 * @return {Request}
		 */
		finalizeRequest: function() {
			const {m_request} = this;
			m_request.setBinary(UPDATED_NODES, this.serialize(), null, true);
			return m_request;
		},

		/**
		 * Return cached node, if it exists, on which a change will be applied
		 * @param {String} url
		 * @return {Node}
		 */
		getCachedNode: function(url) {
			const node = this.cache?.get(url);

			if (!node) {
				this._cannotCacheChange();
				return null;
			}

			// update published time of node to last known server time plus 1 since it will be changed
			node.$PT = csi.getCurrentTime();

			return node;
		},

		isChanged: function() {
			return this.bChanged;
		},

		/**
		 * Set the writer has been changed
		 * @param {Boolean} hasChanged
		 */
		setChanged: function(hasChanged) {
			if (this.bChanged !== hasChanged) {
				this.bChanged = hasChanged;
				this.onChanged?.(hasChanged);
			}
		},

		mixinParameters: function(/*Object*/params) {
			// summary: mixin passed parameters with existing ones (this will overwrite any that already exist)
			this.m_request.mixinParameters(params);
			return this;
		},

		/**
		 * Indicates nodes should not be returned from request
		 */
		noReturnNodes() {
			this.mixinParameters({
				[RETURN_NODES]: "None"
			});
		},

		/**
		 * Replace any occurrences of an old attribute value with a new one
		 * @param {string} oldValue
		 * @param {string} newValue
		 * @return {this}
		 */
		replaceAttributeValue(oldValue, newValue) {
			util.traverseNode(this.doc.documentElement, (e) => {
				for (const {name, value} of e.attributes) {
					if (value === oldValue) {
						e.setAttribute(name, newValue);
					}
				}
			});
		},

		reset: function() {
			// summary: clear changes to writer
			this.bCannotCache = false;
			this.setChanged(false);
			this._cnLast = null;
			xml.removeChildren(this.doc.documentElement);
			this._linkedSpans = new Map();
			return this;
		},

		/**
		 * Return XML string containing operations to be executed
		 * @return {string}
		 */
		serialize() {
			return util.serializeXML(this.doc);
		},

		setCannotCache: function() {
			// summary: indicate that changes to this writer should be published immediately
			this.bCannotCache = true;
		},

		/**
		 * Set mode on writer so that changes by all operations in writer will not be persisted
		 * @return {this}
		 */
		setReport() {
			this.bReport = true;
			this.doc.documentElement.setAttribute("Report", String(true));
			this.m_request.addParameter("ReturnCNLs", "None");
			this.m_request.addParameter("Fmt.Attr.Info", "Max");
			return this;
		},

		callList: function(module, operation, list, parameter, limit) {
			var node = this._newOperation("CallList");

			node.setAttribute(comm.MODULE, module);
			node.setAttribute(comm.OPERATION, operation);
			node.setAttribute(comm.PARAMETER, parameter);
			node.setAttribute("Limit", limit || CALL_LIMIT);

			var doc = this.doc;
			var listNode = xml.e(doc, "List", node);

			array.forEach(list, function(url) {
				xml.e(doc, xml.ATTR_REF, listNode).appendChild(doc.createTextNode(url));
			});

			this._cannotCacheChange();

			return new CallMethod(this, node);
		},

		callMethod: function(module, operation) {
			var node = this._newOperation("Call");

			node.setAttribute(comm.MODULE, module);
			node.setAttribute(comm.OPERATION, operation);

			this._cannotCacheChange();

			return new CallMethod(this, node);
		},

		callQuery: function(/*String*/module, /*String*/operation, /*Query*/query, /*String*/param) {
			var node = this._newOperation("CallQuery");

			node.setAttribute(comm.MODULE, module);
			node.setAttribute(comm.OPERATION, operation);
			node.setAttribute(comm.PARAMETER, param);

			node.appendChild(query.element.cloneNode(true));

			this._cannotCacheChange();

			return new CallMethod(this, node);
		},

		changeList: function(list, limit, path) {
			if (!list || !list.length) return null;

			var doc = this.doc;
			var node = this._newOperation("ChangeList");
			var listNode = xml.e(doc, "List", node);

			array.forEach(list, function(url) {
				xml.e(doc, xml.ATTR_REF, listNode).appendChild(doc.createTextNode(url));
			});

			node.setAttribute("Limit", limit || CHANGE_LIMIT);
			if (path) node.setAttribute("Path", path);
			this._cannotCacheChange();

			return this._newChangeNode(node);
		},

		changeNode: function(/*String*/url) {
			// summary: create ChangeNode operation
			console.assert(xml.isValidURL(url));

			// include assert for length of arguments in case this was used by BC's to change node flags
			if (arguments.length > 1) {
				apiUtility.deprecated("changeNode only accepts one argument. Use changeNodeFlags to change the node's flags.");
				return null;
			}

			// if changing the same node as in the previous operation, then just use the last change node tag
			if (this._cnLast && (this._cnLast.getURL() == url)) {
				return this._cnLast;
			}

			var node = this._newOperation(CHANGE_NODE);
			node.setAttribute(xml.PROP_URL, url);
			return this._newChangeNode(node);
		},

		changeNodeFlags: function(/*String*/url, /*Number*/flags) {
			// summary: create ChangeNode operation to change node flags
			console.assert(xml.isValidURL(url) && (flags != null));

			var node = this._newOperation(CHANGE_NODE);
			node.setAttribute(xml.PROP_URL, url);
			node.setAttribute(xml.PROP_FG, flags);
			this.setChanged(true);

			// update client node with change
			xml.nodeSetFlags(this.getCachedNode(url), flags);

			return this._newChangeNode(node);
		},

		changeParent: function(urlParent, idAttr, typeAttr, value, bAppend) {
			// summary: add or remove list of values to/form given parent node

			// if removing from map, switch type from map to list; adding to a map is not supported unless you have keys (use changeAttributeKeys)
			if (xml.attributeTypeIsMap(typeAttr) && !bAppend) {
				typeAttr = xml.attributeTypeGetBase(typeAttr) + xml.ATTR_LIST;
			}

			return this.changeNode(urlParent)
				.changeAttributeFlag(idAttr, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
				.changeAttribute(idAttr, typeAttr, value, bAppend, !bAppend);
		},

		changeQuery: function(query, limit, path) {
			var node = this._newOperation("ChangeQuery");
			node.setAttribute("Limit", limit || CHANGE_LIMIT);
			if (path) node.setAttribute("Path", path);
			node.appendChild(query.element.cloneNode(true));
			this._cannotCacheChange();

			return this._newChangeNode(node);
		},

		copyNode: function(url, urlFrom, bShallow) {
			var node = this._newOperation("CopyNode");

			node.setAttribute(xml.PROP_URL, url);
			node.setAttribute("FromURL", urlFrom);
			node.setAttribute("NoReflect", !!bShallow);

			this._cannotCacheChange();

			return new CallMethod(this, node);
		},

		/**
		 * For each result node that matches the given query, create a new node of given type and establish
		 * reference from result node to the new node using the given attribute id
		 * @param {Query} query
		 * @param {string} type
		 * @param {string} idAttr
		 * @param {number} [limit]
		 * @return {ChangeNode}
		 */
		createChildQuery: function(query, type, idAttr, limit) {
			var createNode = this.createNode(null, type);
			var node = this._newOperation("CreateChildQuery");
			node.setAttribute("ParentAttribute", idAttr);
			node.setAttribute("Limit", limit || CREATE_LIMIT);
			node.appendChild(query.element.cloneNode(true));
			node.appendChild(createNode.element);
			return createNode;
		},

		createNode: function(url, type, name, flags, bChangeIfExists) {
			var node = this._newOperation(CREATE_NODE);

			if (url)
				node.setAttribute(xml.PROP_URL, url);

			if (name)
				node.setAttribute(xml.PROP_NAME, name);

			if (type)
				node.setAttribute(xml.PROP_TYPE, type);

			if (flags != null)
				node.setAttribute(xml.PROP_FG, flags);

			if (bChangeIfExists)
				node.setAttribute("ChangeIfExists", "true");

			this._cannotCacheChange();

			return this._newChangeNode(node);
		},

		createQuery: function() {
			return new Query();
		},

		createUser: function(idUser, url) {
			var node = this._newOperation("CreateUser");

			node.setAttribute(xml.PROP_USERID, idUser);
			node.setAttribute(xml.PROP_SILENT, "false");

			if (url)
				node.setAttribute(xml.PROP_URL, url);

			this._cannotCacheChange();

			return this._newChangeNode(node);
		},

		deleteList: function(list, limit, path, bIgnoreUsage) {
			if (!list || !list.length) return;

			var doc = this.doc;
			var node = this._newOperation("DeleteList");
			var listNode = xml.e(doc, "List", node);

			array.forEach(list, function(url) {
				xml.e(doc, xml.ATTR_REF, listNode).appendChild(doc.createTextNode(url));
			});

			node.setAttribute("Limit", limit || DELETE_LIMIT);

			if (path) {
				node.setAttribute("Path", path);
			}

			if (bIgnoreUsage) {
				_ignoreUsageOnDelete(doc, node);
			}

			this._cannotCacheChange();
			return this;
		},

		deleteNode: function(url, bIgnoreUsage) {
			if (!xml.isValidURL(url)) return;

			// temporary solution to server error - need to remove previous ChangeNode
			// on node being deleted so that node is not returned in server response
			if (this._cnLast && (this._cnLast.getURL() == url)) {
				var e = this._cnLast.element;
				e.parentNode.removeChild(e);
				this._cnLast = null;
			}

			var node = this._newOperation("DeleteNode");
			node.setAttribute(xml.PROP_URL, url);

			if (bIgnoreUsage) {
				_ignoreUsageOnDelete(this.doc, node);
			}

			this._cannotCacheChange();
			return this;
		},

		deleteQuery: function(query, limit, path, bIgnoreUsage) {
			var node = this._newOperation("DeleteQuery");
			node.setAttribute("Limit", limit || DELETE_LIMIT);
			node.appendChild(query.element.cloneNode(true));

			if (path) {
				node.setAttribute("Path", path);
			}

			if (bIgnoreUsage) {
				_ignoreUsageOnDelete(this.doc, node);
			}

			this._cannotCacheChange();
			return this;
		},

		freezeNode: function(url, bFreeze) {
			var node = this._newOperation("FreezeNode");

			node.setAttribute(xml.PROP_URL, url);
			node.setAttribute("Freeze", !!bFreeze);

			this._cannotCacheChange();
			return this;
		},

		flushChanges: function() {
			this._newOperation("FlushChanges");
			return this;
		},

		validateNode: function(/*String*/url, /*Boolean*/bWithAttributes, /*String?*/validationGroup) {
			var node = this._newOperation("ValidateNode");

			node.setAttribute(xml.PROP_URL, url);
			node.setAttribute("WithAttributes", !!bWithAttributes);

			if (validationGroup) {
				node.setAttribute("Group", validationGroup);
			}

			this._cannotCacheChange();
			return this;
		},

		_cannotCacheChange: function() {
			// summary: indicates an operation has been written to the writer that it cannot
			// immediately apply to the cached data, so the cached data will be out of sync
			// unless it is published
			this.setCannotCache();
			this.setChanged(true);
		},

		_newChangeNode: function(node) {
			this._cnLast = new ChangeNode(this, node);
			return this._cnLast;
		},

		_newOperation: function(/*String*/tag) {
			this._captureCurrentSpanContext();

			// update change status if last operation was ChangeNode (only operation that only causes change if operations applied to it)
			if (tag != CHANGE_NODE) {
				this._cnLast = null;
			}

			return xml.e(this.doc, tag, this.doc.documentElement);
		},

		_captureCurrentSpanContext: function() {
			const spanContext = openTelemetryService.getActiveSpan()?.spanContext();
			if (spanContext) {
				this._linkedSpans.set(spanContext.spanId, spanContext);
			}
		}
	});
});